<template>
  <div
    class="admins-only-toggle"
    :class="{ 'admins-only-panel': scope === 'group' }"
  >
    <div v-if="scope === 'group'" class="admins-only-eyebrow">Visibility</div>

    <div class="custom-control custom-switch">
      <input
        type="checkbox"
        class="custom-control-input"
        :id="inputId"
        :checked="isChecked"
        :disabled="lockedByGroup"
        @change="$emit('update:modelValue', $event.target.checked)"
      />
      <label
        class="custom-control-label"
        :class="{ 'pointer-cursor': !lockedByGroup }"
        :for="inputId"
      >
        Show for admins and managers only
        <i v-if="lockedByGroup" class="fas fa-lock admins-only-lock"></i>
      </label>

      <div class="admins-only-body">
        <template v-if="scope === 'group'">
          <p v-if="modelValue" class="admins-only-desc">
            Only super admins and managers will see this group in Oliver.
            <template v-if="promptCount > 0">
              <strong>{{ promptsInsidePhrase }}</strong>
              {{ promptCount === 1 ? "is" : "are" }} hidden from other roles
              too.
            </template>
            <template v-else>
              Any prompts you add to it will be hidden from other roles too.
            </template>
          </p>
          <template v-else>
            <p class="admins-only-desc">
              <template v-if="allRestricted">
                The group itself isn't restricted, but every prompt in it is.
              </template>
              <template v-else-if="promptCount === 0">
                Everyone can see this group. You can restrict individual prompts
                once you add them.
              </template>
              <template v-else-if="someRestricted">
                Everyone can see this group. {{ restrictedPromptCount }} of
                {{ promptCount }} prompts below
                {{ restrictedPromptCount === 1 ? "is" : "are" }} restricted to
                admins and managers.
              </template>
              <template v-else>
                Everyone can see this group. You can still restrict individual
                prompts below.
              </template>
            </p>
            <div v-if="allRestricted" class="admins-only-warning">
              <i class="fas fa-exclamation-circle"></i>
              <span>
                {{ allRestrictedPhrase }} in this group
                {{ promptCount === 1 ? "is" : "are" }} restricted, so other
                roles won't see this group at all. It becomes visible again once
                it has a prompt that isn't restricted.
              </span>
            </div>
          </template>
        </template>

        <p v-else class="admins-only-desc">
          <template v-if="lockedByGroup">
            Set by the group. Turn off the group's visibility setting to control
            prompts one by one.
          </template>
          <template v-else-if="modelValue">
            Only super admins and managers will see this prompt in Oliver.
          </template>
          <template v-else>
            Everyone who can see this group will see this prompt.
          </template>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "admins-only-toggle",
  emits: ["update:modelValue"],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    inputId: {
      type: String,
      required: true,
    },
    scope: {
      type: String,
      default: "prompt",
    },
    // Prompt scope: the parent group is admins-only, which overrides the
    // prompt's own setting. The prompt's stored value is left untouched so it
    // comes back into effect if the group setting is turned off.
    lockedByGroup: {
      type: Boolean,
      default: false,
    },
    restrictedPromptCount: {
      type: Number,
      default: 0,
    },
    promptCount: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    isChecked() {
      return this.lockedByGroup || this.modelValue;
    },
    allRestricted() {
      return (
        this.promptCount > 0 && this.restrictedPromptCount === this.promptCount
      );
    },
    someRestricted() {
      return this.restrictedPromptCount > 0 && !this.allRestricted;
    },
    promptsInsidePhrase() {
      if (this.promptCount === 1) return "The prompt inside it";
      if (this.promptCount === 2) return "Both prompts inside it";
      return `All ${this.promptCount} prompts inside it`;
    },
    allRestrictedPhrase() {
      if (this.promptCount === 1) return "The only prompt";
      if (this.promptCount === 2) return "Both prompts";
      return `All ${this.promptCount} prompts`;
    },
  },
};
</script>

<style scoped lang="scss">
.admins-only-panel {
  background: var(--light);
  border: 1px solid rgba(149, 170, 201, 0.35);
  border-radius: 10px;
  padding: 14px 18px 16px;
}

@media (max-width: 575.98px) {
  .admins-only-panel {
    padding: 12px 14px 14px;
  }
}

.admins-only-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: var(--gray);
  text-transform: uppercase;
  margin-bottom: 6px;
}

.admins-only-lock {
  font-size: 11px;
  margin-left: 4px;
}

.admins-only-desc {
  font-size: 12.5px;
  color: var(--main-text-color);
  opacity: 0.65;
  margin: 4px 0 0;
}

.admins-only-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 14px;
  font-size: 12.5px;
  border-radius: 8px;
  background: rgba(245, 166, 35, 0.12);
  border: 1px solid rgba(245, 166, 35, 0.4);

  i {
    color: #d98c00;
    margin-top: 2px;
  }
}
</style>
